import type { VNode } from 'preact';
import { ImagePlaceholder } from './ImagePlaceholder';

interface ContentSectionProps {
  title?: string;
  /** Number of image placeholders: 1 (full width) or 2 (side by side) */
  imageCount?: 1 | 2;
  /** Background variant for alternating sections */
  variant?: 'white' | 'grey';
  /** Background image URL */
  backgroundImage?: string;
  /** Background position for the section image */
  backgroundPosition?: string;
  /** Background size for the section image */
  backgroundSize?: string;
  /** Background repeat for the section image */
  backgroundRepeat?: string;
  /** Additional className for the section */
  className?: string;
  /** Section id for scroll-to-hash / deep linking */
  id?: string;
  children?: VNode | VNode[];
}

const LOREM_IPSUM =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit. In egestas nisl non molestie porttitor. Vivamus ullamcorper elit convallis felis rutrum, at aliquet libero porta. Aenean molestie est a felis faucibus, eget sollicitudin turpis dignissim. Nulla sit amet cursus arcu, ut interdum felis. Aliquam non nisi viverra risus gravida vehicula. Interdum et malesuada fames ac ante ipsum primis in faucibus. Quisque facilisis sem neque, quis faucibus neque vestibulum eu. Cras tincidunt a mi quis fringilla. Maecenas accumsan posuere dolor at euismod. Cras ante eros, fringilla ut ligula sed, maximus molestie urna. Integer eget mollis tortor. Nunc molestie mi neque, at accumsan lacus egestas luctus. Pellentesque nibh urna, rutrum eu elit eu, posuere elementum dolor. Integer vitae sem in dui malesuada aliquam. Praesent scelerisque massa sit amet aliquam malesuada. Etiam cursus ullamcorper leo sed pulvinar. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus.';

export function ContentSection({
  title,
  imageCount = 1,
  variant = 'white',
  backgroundImage,
  backgroundPosition = 'center',
  backgroundSize = 'cover',
  backgroundRepeat = 'no-repeat',
  className = '',
  id,
  children,
}: ContentSectionProps): VNode {
  // const hasChildren = Array.isArray(children) ? children.length > 0 : Boolean(children);
  const bgClass = variant === 'grey' ? 'bg-neutral-100' : 'bg-white';

  const sectionStyle = backgroundImage
    ? {
        backgroundImage: `url(${backgroundImage})`,
        backgroundPosition,
        backgroundSize,
        backgroundRepeat,
      }
    : undefined;

  return (
    <section
      id={id}
      className={`w-full  ${bgClass} ${className}`}
      style={sectionStyle}
    >
      <div className="px-5 mx-auto max-w-[1170px] sm:max-w-[1194px] 2xl:max-w-[1226px] sm:px-8 2xl:px-12 py-12 sm:py-20 2xl:py-[120px]">
        <div className="flex flex-col gap-8 sm:gap-10">
          {title && <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 tracking-tight uppercase">
            {title}
          </h2>}

          {!children && (
            <>
              {/* Two-column text layout - full width to match images */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 w-full">
                <p className="text-sm sm:text-base text-neutral-500 leading-relaxed">{LOREM_IPSUM}</p>
                <p className="text-sm sm:text-base text-neutral-500 leading-relaxed">{LOREM_IPSUM}</p>
              </div>

              {/* Image placeholder(s) */}
              <div
                className={
                  imageCount === 2
                    ? 'grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6'
                    : 'w-full'
                }
              >
                {imageCount === 1 ? (
                  <ImagePlaceholder aspectRatio="16/9" />
                ) : (
                  <>
                    <ImagePlaceholder aspectRatio="16/9" />
                    <ImagePlaceholder aspectRatio="16/9" />
                  </>
                )}
              </div>
            </>
          )}

          {children}
        </div>
      </div>
    </section>
  );
}
